/**
 * Debug utilities for API requests.
 * Use these in browser console to test if headers are being sent.
 */

import { api } from './api';
import { auth } from './auth';
import { settingsApi } from './api/typedClient';
import { log } from './logger';

/**
 * Test if Authorization header is being sent.
 * Run in browser console: window.testAuthHeader()
 */
export async function testAuthHeader() {
  log.debug('API', '--- Testing Authorization Header ---');

  const token = auth.getToken();
  log.debug('API', 'Token in localStorage:', token ? 'Found' : 'Missing');

  if (!token) {
    log.error('API', 'No token found! Cannot test header.');
    log.debug('API', 'Action: User needs to authenticate via Strava OAuth');
    return;
  }

  log.debug('API', 'Token length:', token.length);
  log.debug('API', 'Token preview:', token.substring(0, 50) + '...');

  // Check if token is expired
  if (auth.isTokenExpired()) {
    log.error('API', 'Token is EXPIRED!');
    log.debug('API', 'Action: User needs to reconnect Strava');
    return;
  }

  log.debug('API', 'Token is valid');

  // Test with debug endpoint (if available)
  try {
    log.debug('API', 'Testing with debug endpoint...');
    const response = await api.get('/debug/headers');
    log.debug('API', 'Response:', response);

    if (response && typeof response === 'object') {
      const debugData = response as { authorization_header?: string; authorization_value?: string; all_headers?: Record<string, string> };

      if (debugData.authorization_header === 'MISSING' || !debugData.authorization_header) {
        log.error('API', 'Authorization header is NOT being sent!');
        log.error('API', 'Fix: Check axios interceptor configuration');
      } else {
        log.debug('API', 'Authorization header is being sent');
        log.debug('API', 'Header value preview:', debugData.authorization_value?.substring(0, 30) + '...');
      }

      if (debugData.all_headers) {
        log.debug('API', 'All headers received:', debugData.all_headers);
      }
    }
  } catch (error) {
    log.warn('API', 'Debug endpoint not available, testing with profile endpoint...');

    // Fallback: test with actual endpoint
    try {
      const profileResponse = await settingsApi.getProfile();
      const profileData = profileResponse.data || profileResponse;
      log.debug('API', 'Profile request succeeded - header is being sent!');
      log.debug('API', 'Profile data:', profileData);
    } catch (profileError) {
      const apiError = profileError as { status?: number; response?: { status?: number } };
      const status = apiError.status || apiError.response?.status;

      if (status === 401) {
        log.error('API', 'Got 401 Unauthorized - header might not be sent correctly');
        log.error('API', 'Error:', profileError);
      } else {
        log.error('API', 'Request failed:', profileError);
      }
    }
  }
}

/**
 * Test raw fetch with Authorization header.
 * Run in browser console: window.testRawFetch()
 */
export async function testRawFetch() {
  log.debug('API', '--- Testing Raw Fetch with Authorization Header ---');

  const token = auth.getToken();
  if (!token) {
    log.error('API', 'No token found!');
    return;
  }

  const baseURL = typeof window !== 'undefined'
    ? (import.meta.env.VITE_API_URL || window.location.origin)
    : 'http://localhost:8000';

  const testUrl = `${baseURL}/debug/headers`;

  log.debug('API', 'Testing URL:', testUrl);
  log.debug('API', 'Token:', token.substring(0, 30) + '...');

  try {
    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Required for HTTP-only cookies
    });

    const data = await response.json();
    log.debug('API', 'Response status:', response.status);
    log.debug('API', 'Response data:', data);

    if (data.authorization_header === 'MISSING') {
      log.error('API', 'Header is MISSING in raw fetch too!');
    } else {
      log.debug('API', 'Header is present in raw fetch');
    }
  } catch (error) {
    log.error('API', 'Fetch failed:', error);
  }
}

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as { testAuthHeader?: () => Promise<void>; testRawFetch?: () => Promise<void> }).testAuthHeader = testAuthHeader;
  (window as { testAuthHeader?: () => Promise<void>; testRawFetch?: () => Promise<void> }).testRawFetch = testRawFetch;
}
